/**
 * Debouncing and throttling utilities for preventing race conditions
 * and improving application stability
 */

/**
 * Debounce function - delays execution until after wait time has elapsed
 * since the last time it was invoked
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }

    const callNow = immediate && !timeout

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)

    if (callNow) func(...args)
  }
}

/**
 * Throttle function - limits execution to at most once per wait time
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  let lastFunc: NodeJS.Timeout
  let lastRan: number

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      lastRan = Date.now()
      inThrottle = true
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= wait) {
          func(...args)
          lastRan = Date.now()
        }
      }, Math.max(wait - (Date.now() - lastRan), 0))
    }
  }
}

/**
 * Async debounce for promises - cancels previous pending promises
 */
export function debounceAsync<T extends (...args: any[]) => Promise<any>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout | null = null
  let resolveReject: { resolve: (...args: any[]) => void; reject: (...args: any[]) => void } | null = null

  return function executedFunction(...args: Parameters<T>): Promise<ReturnType<T>> {
    return new Promise((resolve, reject) => {
      // Cancel previous promise
      if (resolveReject) {
        resolveReject.reject(new Error('Debounced: Previous call cancelled'))
      }

      // Clear previous timeout
      if (timeout) {
        clearTimeout(timeout)
      }

      // Store current promise resolvers
      resolveReject = { resolve, reject }

      // Set new timeout
      timeout = setTimeout(async () => {
        try {
          const result = await func(...args)
          if (resolveReject) {
            resolveReject.resolve(result)
            resolveReject = null
          }
        } catch (error) {
          if (resolveReject) {
            resolveReject.reject(error)
            resolveReject = null
          }
        }
      }, wait)
    })
  }
}

/**
 * Request deduplication - prevents multiple identical requests
 */
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>()

  /**
   * Deduplicate requests by key - returns existing promise if request is already pending
   */
  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>,
    ttl: number = 5000
  ): Promise<T> {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key) as Promise<T>
    }

    // Create new request
    const promise = requestFn()
    this.pendingRequests.set(key, promise)

    // Clean up after completion or timeout
    const cleanup = () => {
      this.pendingRequests.delete(key)
    }

    promise.then(cleanup, cleanup)

    // Set TTL cleanup
    setTimeout(cleanup, ttl)

    return promise
  }

  /**
   * Clear all pending requests
   */
  clear(): void {
    this.pendingRequests.clear()
  }

  /**
   * Get number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size
  }
}

// Global request deduplicator instance
export const requestDeduplicator = new RequestDeduplicator()

/**
 * Button click protection - prevents rapid clicking
 */
export function createClickProtection(
  onClick: () => void | Promise<void>,
  cooldown: number = 1000
): () => void {
  let isProcessing = false
  let lastClick = 0

  return async () => {
    const now = Date.now()
    
    // Check cooldown
    if (now - lastClick < cooldown) {
      console.warn('Click ignored: Still in cooldown period')
      return
    }

    // Check if already processing
    if (isProcessing) {
      console.warn('Click ignored: Previous action still processing')
      return
    }

    try {
      isProcessing = true
      lastClick = now
      await onClick()
    } catch (error) {
      console.error('Protected click handler error:', error)
    } finally {
      isProcessing = false
    }
  }
}

/**
 * Loading state manager for components
 */
export class LoadingStateManager {
  private loadingStates = new Map<string, boolean>()
  private listeners = new Set<(states: Record<string, boolean>) => void>()

  /**
   * Set loading state for a specific operation
   */
  setLoading(operation: string, loading: boolean): void {
    this.loadingStates.set(operation, loading)
    this.notifyListeners()
  }

  /**
   * Get loading state for a specific operation
   */
  isLoading(operation: string): boolean {
    return this.loadingStates.get(operation) || false
  }

  /**
   * Check if any operation is loading
   */
  isAnyLoading(): boolean {
    return Array.from(this.loadingStates.values()).some(loading => loading)
  }

  /**
   * Get all loading states
   */
  getAllStates(): Record<string, boolean> {
    return Object.fromEntries(this.loadingStates)
  }

  /**
   * Subscribe to loading state changes
   */
  subscribe(listener: (states: Record<string, boolean>) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Clear all loading states
   */
  clear(): void {
    this.loadingStates.clear()
    this.notifyListeners()
  }

  private notifyListeners(): void {
    const states = this.getAllStates()
    this.listeners.forEach(listener => listener(states))
  }
}

// Global loading state manager
export const globalLoadingManager = new LoadingStateManager()

/**
 * Hook for using loading states in React components
 */
export function useLoadingState(operation: string) {
  const [isLoading, setIsLoading] = React.useState(
    globalLoadingManager.isLoading(operation)
  )

  React.useEffect(() => {
    const unsubscribe = globalLoadingManager.subscribe((states) => {
      setIsLoading(states[operation] || false)
    })
    return unsubscribe
  }, [operation])

  const setLoading = React.useCallback((loading: boolean) => {
    globalLoadingManager.setLoading(operation, loading)
  }, [operation])

  return [isLoading, setLoading] as const
}

// React import for the hook
import React from 'react'
